'use client';

import React, { useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Dialog, DialogContentWrapper, DialogTitle } from '@/comps/primitives/dialog';
import { Button } from '@/comps/primitives/button';
import { useTourContext } from '@/domains/tour/TourContext';
import { useShellTourOptions } from '@/domains/tour/use-shell-tour-options';
import { usePathname } from 'next/navigation';
import { useAuthContext } from '@/hooks/use-auth';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

export function WelcomeBanner() {
  const { isSharedLink } = useAuthContext();
  const { tourStatus, startTour, skipTour } = useTourContext();
  const shellTourOptions = useShellTourOptions();
  const pathname = usePathname();
  const [open, setOpen] = useState(tourStatus === null);
  const startingTourRef = useRef(false);

  if (tourStatus !== null || pathname === '/onboarding') return null;

  const handleStartTour = () => {
    if (!shellTourOptions) return;
    startingTourRef.current = true;
    setOpen(false);
    startTour(shellTourOptions);
  };

  const handleSkip = () => {
    setOpen(false);
    skipTour();
  };

  if (isSharedLink) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        if (!val) {
          if (startingTourRef.current) {
            startingTourRef.current = false;
            return;
          }
          handleSkip();
        }
      }}
    >
      <DialogContentWrapper className="max-w-md p-0 shadow-2xl" onInteractOutside={(e) => e.preventDefault()}>
        <VisuallyHidden>
          <DialogTitle>Welcome to the New Nebuly</DialogTitle>
        </VisuallyHidden>
        <div className="flex flex-col items-center gap-6 p-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent ring-1 ring-border">
            <Sparkles className="h-7 w-7 text-foreground" />
          </div>

          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-bold text-foreground">Welcome to the New Nebuly</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              The platform has been redesigned with a focus on reports and data exploration. Enjoy better clarity and
              faster access to your insights.
            </p>
          </div>

          <div className="flex w-full flex-col gap-3">
            <Button className="w-full rounded-lg" onClick={handleStartTour} disabled={!shellTourOptions}>
              Show me around →
            </Button>
            <Button variant="ghost" className="w-full text-muted-foreground hover:text-foreground" onClick={handleSkip}>
              Skip for now
            </Button>
          </div>
        </div>
      </DialogContentWrapper>
    </Dialog>
  );
}
